import React from "react";
import { Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import _ from "lodash";
import { formatCurrencyUSD, formatNumberPdf } from "../../../utils/formattingUtils";
import { getFooter, getSalientFactsTableConfig } from "../../../utils/helperUtils";

const styles = StyleSheet.create({
    page: {
        padding: "40px 50px",
        fontSize: 9,
        position: "relative",
        backgroundColor: "#ffffff",
    },
    heading: {
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 5,
        color: "#2c3e50",
    },
    section: {
        marginBottom: 10,
    },
    tableCell: {
        textAlign: "left",
        fontSize: 8,
        padding: "3px 4px",
        borderBottom: "1px solid #e0e0e0",
        whiteSpace: "wrap",
        wordWrap: "break-word",
    },
    tableHeader: {
        backgroundColor: "#f7f6f8",
        fontWeight: "black",
        fontSize: 12,
        padding: "4px 3px",
    },
    alternateRow: {
        backgroundColor: "#f8f9fa",
    },
    firstColumn: {
        maxWidth: "30%",
    },
    marketValueHeader: {
        backgroundColor: "#34495e",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: 8,
        padding: "6px 4px",
    },
    valueBasedHeader: {
        backgroundColor: "#2980b9",
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: 8,
        padding: "4px 3px",
    },
    keyIssueText: {
        fontSize: 8,
        marginBottom: 3,
        lineHeight: 1.0,
    },
    mainContainer: {
        flex: 1,
        paddingBottom: 30,
    },
});

export const SalientFactsPage = ({ data, confidentialInfo }) => {
    const { exportAppealsPackage } = data.appealApproachCalculationData;
    const { valueBasedSale } = exportAppealsPackage;
    const tableConfig = getSalientFactsTableConfig;
    const getColumnWidth = index => {
        // Column width distribution (total 100%)
        const columnWidths = {
            0: "19%", // Parcel Number
            1: "12%", // Land Size
            2: "12%", // Building Size
            3: "11%", // Land Value
            4: "8%", // Land Value/SF
            5: "11%", // Improvements
            6: "8%", // Improvements/SF
            7: "11%", // Total
            8: "8%", // Total/SF
        };

        return columnWidths[index] || "auto";
    };

    const renderValueBasedSaleTable = yearData => (
        <Table style={{ border: "none" }}>
            <TH>
                <TD
                    style={{
                        ...styles.tableCell,
                        ...styles.tableHeader,
                        width: "100%",
                        textAlign: "left",
                        borderRight: "none", // Remove right border
                    }}
                >
                    {yearData.title}
                </TD>
            </TH>
            <TR>
                {tableConfig.valueBasedTable.headers.map((header, index) => (
                    <TD
                        key={index}
                        style={{
                            ...styles.tableCell,
                            minWidth: getColumnWidth(index),
                        }}
                    >
                        {header}
                    </TD>
                ))}
            </TR>
            <TR>
                {[
                    yearData.parcelNumber,
                    formatNumberPdf(yearData.landSize),
                    formatNumberPdf(yearData.buildingSize),
                    formatCurrencyUSD(yearData.landValue),
                    formatCurrencyUSD(yearData.landValuePerSqFt),
                    formatCurrencyUSD(yearData.improvements),
                    formatCurrencyUSD(yearData.improvementsPerSqFt),
                    formatCurrencyUSD(yearData.total),
                    formatCurrencyUSD(yearData.totalPerSqFt),
                ].map((value, index) => (
                    <TD
                        key={index}
                        style={{
                            ...styles.tableCell,
                            minWidth: getColumnWidth(index),
                        }}
                    >
                        {value}
                    </TD>
                ))}
            </TR>
        </Table>
    );

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.mainContainer}>
                <View style={styles.section}>
                    <Table style={{ border: "none" }}>
                        <TH style={{ fontWeight: "ultrabold" }}>
                            <TD
                                style={{
                                    ...styles.tableCell,
                                    ...styles.tableHeader,
                                    width: "100%",
                                }}
                            >
                                Summary of Salient Facts
                            </TD>
                        </TH>
                        {tableConfig.salientFacts.map((item, index) => (
                            <TR key={index}>
                                <TD style={{ ...styles.tableCell, ...styles.firstColumn }}>
                                    {item.label}
                                </TD>
                                <TD
                                    style={{
                                        ...styles.tableCell,
                                    }}
                                >
                                    {item.format(_.get(data, item.key, ""))}
                                </TD>
                            </TR>
                        ))}
                    </Table>
                </View>

                <View style={styles.section}>
                    {renderValueBasedSaleTable(valueBasedSale.previousYear)}
                </View>

                <View style={styles.section}>
                    {renderValueBasedSaleTable(valueBasedSale.currentYear)}
                </View>

                <View style={styles.section}>
                    <Table style={{ border: "none" }}>
                        <TH>
                            <TD
                                style={{
                                    ...styles.tableCell,
                                    ...styles.tableHeader,
                                    width: "100%",
                                    textAlign: "left",
                                    borderRight: "none",
                                }}
                            >
                                Market Value Indication
                            </TD>
                        </TH>
                        <TR>
                            {tableConfig.marketValueIndication.headers.map((header, index) => (
                                <TD key={index} style={{ ...styles.tableCell }}>
                                    {header}
                                </TD>
                            ))}
                        </TR>
                        {tableConfig.marketValueIndication.rows.map((row, index) => {
                            const values = row.getValue(data);

                            return (
                                <TR key={index}>
                                    <TD style={{ ...styles.tableCell }}>{row.type}</TD>
                                    {[
                                        values.land,
                                        values.landValuePerSqFt,
                                        values.improvements,
                                        values.improvementsPerSqFt,
                                        values.total,
                                        values.totalPerSqFt,
                                    ].map((value, cellIndex) => (
                                        <TD
                                            key={cellIndex}
                                            style={{
                                                ...styles.tableCell,
                                            }}
                                        >
                                            {tableConfig.marketValueIndication.formatMoney(value)}
                                        </TD>
                                    ))}
                                </TR>
                            );
                        })}
                    </Table>
                </View>

                <View style={styles.section}>
                    <Text style={styles.heading}>Key Issues or Concerns:</Text>
                    {exportAppealsPackage.keyIssues.map((issue, index) => (
                        <Text key={index} style={styles.keyIssueText}>
                            • {issue}
                        </Text>
                    ))}
                </View>
            </View>

            {getFooter(confidentialInfo)}
        </Page>
    );
};
